import { ArrowLeft, Download, Lock } from 'lucide-react';
import { useMutation } from '@platform/api-client';
import { useAuth } from '@platform/auth-client';
import { Badge, browserTimeZone, Button, ConfirmDialog, formatMoney } from '@platform/design-system';
import type { ShippingClient } from '@platform/openapi';
import { useState } from 'react';
import { ResourceTable } from '../ResourceTable.tsx';
import { carrierNameOf, useCarriers } from '../settings/carriers.ts';
import { usePageContext } from '../usePageContext.ts';
import { HANDOVER_ROLE, refusalKeyOf, useHandover } from './handover.ts';
import { useParcelColumns } from './parcel-columns.tsx';
import { ReadyParcels } from './ReadyParcels.tsx';
import styles from './Handover.module.css';
import { ScanField } from '../ScanField.tsx';
import { ScanOutcome, type ScanResult } from '../ScanOutcome.tsx';
import { useVoucherDownload } from './useVoucherDownload.ts';

export type HandoverSessionProps = { shipping: ShippingClient; handoverId: string; onBack: () => void };

export const HandoverSession = ({ shipping, handoverId, onBack }: HandoverSessionProps) => {
  const { t, locale, invalidate, describeError } = usePageContext();
  const canHandOver = useAuth().hasRole(HANDOVER_ROLE);
  const detail = useHandover(shipping, handoverId);
  const carriers = useCarriers(shipping);
  const voucher = useVoucherDownload(shipping);
  const [outcome, setOutcome] = useState<ScanResult | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [search, setSearch] = useState('');
  const explain = (error: unknown): string => {
    const key = refusalKeyOf(error);
    return key === null ? (describeError(error) ?? '') : t(key);
  };
  const scan = useMutation({
    mutationFn: (trackingNumber: string) =>
      shipping.post('/admin/handovers/{id}/parcels', { path: { id: handoverId }, body: { trackingNumber } }),
    onSuccess: async (_handover, trackingNumber) => {
      setOutcome({ tone: 'accepted', message: t('handover.accepted', { trackingNumber }) });
      await invalidate('handovers');
    },
    onError: (error, trackingNumber) => {
      setOutcome({ tone: 'refused', message: `${trackingNumber} : ${explain(error)}` });
    },
  });
  const close = useMutation({
    mutationFn: () =>
      shipping.post('/admin/handovers/{id}/close', {
        path: { id: handoverId },
        body: { timeZone: browserTimeZone() },
      }),
    onSuccess: async () => {
      await invalidate('handovers');
      voucher.mutate(handoverId);
    },
  });

  const handover = detail.data?.handover;
  const carrierName = carrierNameOf(carriers.data?.items, handover?.carrierCode ?? '');
  const isOpen = handover?.status === 'open';

  const columns = useParcelColumns();

  return (
    <div className={styles['panel']}>
      <div className={styles['actions']}>
        <Button
          icon={<ArrowLeft size={20} aria-hidden="true" className="flip-inline" />}
          variant="ghost"
          onClick={onBack}
        >
          {t('handover.back')}
        </Button>
      </div>
      {handover === undefined ? null : (
        <div className={styles['summary']}>
          <strong>{carrierName}</strong>
          <Badge tone={isOpen ? 'warning' : 'success'}>{t(`handover.status_${handover.status}`)}</Badge>
          <span>{t('handover.countValue', { count: handover.parcelCount })}</span>
          {handover.codTotal === null ? null : <strong>{formatMoney(handover.codTotal, locale)}</strong>}
        </div>
      )}
      {isOpen && canHandOver ? (
        <div className={styles['card']}>
          <ScanField
            label={t('handover.scanLabel')}
            helperText={t('handover.scanHelp')}
            submitLabel={t('handover.add')}
            busy={scan.isPending}
            onScan={(trackingNumber) => {
              scan.mutate(trackingNumber);
            }}
            onTextChange={setSearch}
          />
          <ScanOutcome result={outcome} />
          <ReadyParcels
            shipping={shipping}
            handoverId={handoverId}
            search={search}
            busy={scan.isPending}
            onAdd={(trackingNumber) => {
              scan.mutate(trackingNumber);
            }}
          />
        </div>
      ) : null}
      <ResourceTable
        query={detail}
        columns={columns}
        rows={detail.data?.parcels ?? []}
        rowKey={(row) => row.shipmentId}
        emptyTitle={t('handover.empty')}
        emptyDescription={isOpen ? t('handover.emptyHelp') : undefined}
      />
      {canHandOver && handover !== undefined ? (
        <div className={styles['actions']}>
          {isOpen ? (
            <Button
              icon={<Lock size={20} aria-hidden="true" />}
              variant="accent"
              disabled={handover.parcelCount === 0 || close.isPending}
              onClick={() => {
                setConfirming(true);
              }}
            >
              {t('handover.close')}
            </Button>
          ) : (
            <Button
              icon={<Download size={20} aria-hidden="true" />}
              disabled={voucher.isPending}
              onClick={() => {
                voucher.mutate(handoverId);
              }}
            >
              {t('handover.downloadVoucher')}
            </Button>
          )}
        </div>
      ) : null}
      {close.isError ? <p role="alert">{explain(close.error)}</p> : null}
      {voucher.isError ? <p role="alert">{describeError(voucher.error)}</p> : null}
      {confirming ? (
        <ConfirmDialog
          title={t('handover.closeTitle')}
          message={t('handover.closeMessage', { count: handover?.parcelCount ?? 0, carrier: carrierName })}
          confirmLabel={t('handover.closeConfirm')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          onCancel={() => {
            setConfirming(false);
          }}
          onConfirm={() => {
            setConfirming(false);
            close.mutate();
          }}
        />
      ) : null}
    </div>
  );
};
